import { useState, useEffect, useMemo } from 'react';
import { useRownd } from '@rownd/react';
import { 
  Box, 
  Typography, 
  Grid,
  Button,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Container,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Collapse,
  FormHelperText,
  IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TranslateIcon from '@mui/icons-material/Translate';
import InputField from '../components/InputField';
import { submitReport } from '../utils/api';

// API Base URL
const API_BASE_URL = 'https://tsunami-data.robert-9e7.workers.dev/api';

// Wave direction options
const WAVE_DIRECTIONS = [
  "North", "Northeast", "East", "Southeast",
  "South", "Southwest", "West", "Northwest"
];

// Wave direction options in Tagalog
const WAVE_DIRECTIONS_TAGALOG = [
  "Hilaga", "Hilagang-Silangan", "Silangan", "Timog-Silangan",
  "Timog", "Timog-Kanluran", "Kanluran", "Hilagang-Kanluran"
];

// Ship status options
const SHIP_STATUSES = [
  "IN TRANSIT", "DIVERTED", "AWAITING TOW", 
  "ANCHORED", "OPERATIONAL", "DAMAGED"
];

// Ship status options in Tagalog
const SHIP_STATUSES_TAGALOG = [
  "NASA BIYAHE", "NAPALIHIS", "NAGHIHINTAY NG TOW", 
  "NAKA-ANGKLA", "GUMAGANA", "SIRA"
];

// Translations
const translations = {
  english: {
    pageTitle: "Submit Tsunami Report",
    authRequired: "Authentication Required",
    signInText: "You need to sign in to submit tsunami reports for the safety of all users.",
    signInButton: "Sign In to Continue",
    basicInfo: "Basic Information",
    requiredField: "* Required field",
    describeLabel: "Describe what you saw *",
    describeOptionalLabel: "Describe what you saw (optional with SOS)",
    describePlaceholder: "Provide a detailed description of the tsunami you observed",
    sosLabel: "SOS - Emergency situation requiring immediate assistance",
    damageReport: "Damage Report",
    damageCheckbox: "Report property or infrastructure damages",
    damageDetails: "Damage Details",
    damagePlaceholder: "Describe the damages you observed (buildings, roads, infrastructure, etc.)",
    shipInfo: "Ship Information",
    reportFromShip: "I am reporting from a ship",
    shipStatus: "Ship Status *",
    selectStatus: "Select status",
    shipStatusRequired: "Ship status is required when reporting from a ship",
    waveCharacteristics: "Wave Characteristics (Optional)",
    optionalFields: "All fields in this section are optional but help provide a more complete report",
    severity: "Severity (0-10)",
    waveHeight: "Wave Height (meters)",
    waterLevel: "Water Level",
    waveDirection: "Wave Direction",
    selectDirection: "Select direction",
    location: "Location",
    locationDesc: "Your current location will be included in the report unless you opt out.",
    getCurrentLocation: "Get Current Location",
    uploadImage: "Upload Image (Optional)",
    drag: "Drag and drop an image here, or click to select",
    imageRestriction: "Only images up to 5MB are accepted",
    submit: "Submit Report",
    submitting: "Submitting...",
    confirmSubmission: "Confirm Submission",
    confirmText: "Are you sure you want to submit this report? Once submitted, it cannot be modified.",
    locationIncluded: "Your current location will be included in this report.",
    locationNotAvailable: "Your location is not available and will not be included in this report.",
    cancel: "Cancel",
    confirmButton: "Yes, Submit",
    successDefault: "Report submitted successfully!",
    successSOS: "SOS report submitted successfully! Emergency services have been notified.",
    switchToEnglish: "Switch to English",
    switchToTagalog: "Switch to Tagalog",
    switchToVietnamese: "Switch to Vietnamese",
    presetLocations: "Preset Locations",
    presetLocationsDesc: "Click a button to use a predefined location instead of your current position",
    manila: "Manila",
    cebu: "Cebu",
    davao: "Davao"
  },
  tagalog: {
    pageTitle: "Magsumite ng Ulat ng Tsunami",
    authRequired: "Kailangan ng Pagkakakilanlan",
    signInText: "Kailangan mong mag-sign in upang magsumite ng mga ulat ng tsunami para sa kaligtasan ng lahat ng mga gumagamit.",
    signInButton: "Mag-sign in upang Magpatuloy",
    basicInfo: "Pangunahing Impormasyon",
    requiredField: "* Kinakailangang field",
    describeLabel: "Ilarawan ang iyong nakita *",
    describeOptionalLabel: "Ilarawan ang iyong nakita (opsyonal sa SOS)",
    describePlaceholder: "Magbigay ng detalyadong paglalarawan ng tsunami na iyong naobserbahan",
    sosLabel: "SOS - Sitwasyon na nangangailangan ng agarang tulong",
    damageReport: "Ulat ng Pinsala",
    damageCheckbox: "Mag-ulat ng mga pinsala sa ari-arian o imprastruktura",
    damageDetails: "Mga Detalye ng Pinsala",
    damagePlaceholder: "Ilarawan ang mga pinsalang iyong naobserbahan (mga gusali, kalsada, imprastruktura, atbp.)",
    shipInfo: "Impormasyon ng Barko",
    reportFromShip: "Nag-uulat ako mula sa isang barko",
    shipStatus: "Katayuan ng Barko *",
    selectStatus: "Pumili ng katayuan",
    shipStatusRequired: "Kinakailangan ang katayuan ng barko kapag nag-uulat mula sa barko",
    waveCharacteristics: "Mga Katangian ng Alon (Opsyonal)",
    optionalFields: "Ang lahat ng mga field sa seksyong ito ay opsyonal ngunit tumutulong sa pagbibigay ng mas kumpletong ulat",
    severity: "Kalubhaan (0-10)",
    waveHeight: "Taas ng Alon (metro)",
    waterLevel: "Antas ng Tubig",
    waveDirection: "Direksiyon ng Alon",
    selectDirection: "Pumili ng direksiyon",
    location: "Lokasyon",
    locationDesc: "Ang iyong kasalukuyang lokasyon ay isasama sa ulat maliban kung pipiliin mong huwag isama.",
    getCurrentLocation: "Kunin ang Kasalukuyang Lokasyon",
    uploadImage: "Mag-upload ng Larawan (Opsyonal)",
    drag: "I-drag at i-drop ang larawan dito, o i-click upang pumili",
    imageRestriction: "Tanging mga larawang hanggang 5MB ang tinatanggap",
    submit: "Isumite ang Ulat",
    submitting: "Isinusumite...",
    confirmSubmission: "Kumpirmahin ang Pagsusumite",
    confirmText: "Sigurado ka bang gusto mong isumite ang ulat na ito? Kapag naisumite na, hindi na ito maaaring baguhin.",
    locationIncluded: "Ang iyong kasalukuyang lokasyon ay isasama sa ulat na ito.",
    locationNotAvailable: "Ang iyong lokasyon ay hindi magagamit at hindi isasama sa ulat na ito.",
    cancel: "Kanselahin",
    confirmButton: "Oo, Isumite",
    successDefault: "Matagumpay na naisumite ang ulat!",
    successSOS: "Matagumpay na naisumite ang ulat ng SOS! Ang mga serbisyong pang-emergency ay naabisuhan na.",
    switchToEnglish: "Lumipat sa Ingles",
    switchToTagalog: "Lumipat sa Tagalog",
    switchToVietnamese: "Lumipat sa Tiếng Việt",
    presetLocations: "Nakatakdang mga Lokasyon",
    presetLocationsDesc: "Mag-click ng button upang gumamit ng predefined na lokasyon sa halip na ang iyong kasalukuyang posisyon",
    manila: "Maynila",
    cebu: "Cebu",
    davao: "Davao"
  },
  vietnamese: {
    pageTitle: "Gửi Báo Cáo Sóng Thần",
    authRequired: "Yêu Cầu Xác Thực",
    signInText: "Bạn cần đăng nhập để gửi báo cáo sóng thần vì sự an toàn của tất cả người dùng.",
    signInButton: "Đăng Nhập để Tiếp Tục",
    basicInfo: "Thông Tin Cơ Bản",
    requiredField: "* Trường bắt buộc",
    describeLabel: "Mô tả những gì bạn đã thấy *",
    describeOptionalLabel: "Mô tả những gì bạn đã thấy (tùy chọn với SOS)",
    describePlaceholder: "Cung cấp mô tả chi tiết về sóng thần bạn đã quan sát",
    sosLabel: "SOS - Tình huống khẩn cấp cần hỗ trợ ngay lập tức",
    damageReport: "Báo Cáo Thiệt Hại",
    damageCheckbox: "Báo cáo thiệt hại tài sản hoặc cơ sở hạ tầng",
    damageDetails: "Chi Tiết Thiệt Hại",
    damagePlaceholder: "Mô tả các thiệt hại bạn đã quan sát (tòa nhà, đường xá, cơ sở hạ tầng, v.v.)",
    shipInfo: "Thông Tin Tàu",
    reportFromShip: "Tôi đang báo cáo từ một con tàu",
    shipStatus: "Trạng Thái Tàu *",
    selectStatus: "Chọn trạng thái",
    shipStatusRequired: "Trạng thái tàu là bắt buộc khi báo cáo từ tàu",
    waveCharacteristics: "Đặc Điểm Sóng (Tùy Chọn)",
    optionalFields: "Tất cả các trường trong phần này là tùy chọn nhưng giúp cung cấp báo cáo đầy đủ hơn",
    severity: "Mức Độ Nghiêm Trọng (0-10)",
    waveHeight: "Chiều Cao Sóng (mét)",
    waterLevel: "Mực Nước",
    waveDirection: "Hướng Sóng",
    selectDirection: "Chọn hướng",
    location: "Vị Trí",
    locationDesc: "Vị trí hiện tại của bạn sẽ được đưa vào báo cáo trừ khi bạn chọn không tham gia.",
    getCurrentLocation: "Lấy Vị Trí Hiện Tại",
    uploadImage: "Tải Lên Hình Ảnh (Tùy Chọn)",
    drag: "Kéo và thả hình ảnh vào đây, hoặc nhấp để chọn",
    imageRestriction: "Chỉ chấp nhận hình ảnh lên đến 5MB",
    submit: "Gửi Báo Cáo",
    submitting: "Đang Gửi...",
    confirmSubmission: "Xác Nhận Gửi",
    confirmText: "Bạn có chắc chắn muốn gửi báo cáo này không? Sau khi gửi, nó không thể sửa đổi.",
    locationIncluded: "Vị trí hiện tại của bạn sẽ được đưa vào báo cáo này.",
    locationNotAvailable: "Vị trí của bạn không khả dụng và sẽ không được đưa vào báo cáo này.",
    cancel: "Hủy Bỏ",
    confirmButton: "Có, Gửi",
    successDefault: "Báo cáo đã được gửi thành công!",
    successSOS: "Báo cáo SOS đã được gửi thành công! Dịch vụ khẩn cấp đã được thông báo.",
    switchToEnglish: "Chuyển sang Tiếng Anh",
    switchToTagalog: "Chuyển sang Tiếng Tagalog",
    switchToVietnamese: "Chuyển sang Tiếng Việt",
    presetLocations: "Vị Trí Đặt Trước",
    presetLocationsDesc: "Nhấp vào nút để sử dụng vị trí đã xác định trước thay vì vị trí hiện tại của bạn",
    manila: "Manila",
    cebu: "Cebu",
    davao: "Davao"
  }
};

export default function TsunamiReportPage() {
  const { is_authenticated, is_initializing, requestSignIn, getAccessToken, user } = useRownd();
  const [multilineValue, setMultilineValue] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState(5);
  const [heightMeters, setHeightMeters] = useState('');
  const [waterLevel, setWaterLevel] = useState('');
  const [waveDirection, setWaveDirection] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coordinates, setCoordinates] = useState({ latitude: null, longitude: null });
  const [locationStatus, setLocationStatus] = useState('pending'); // 'pending', 'success', 'error'
  const [showLocationMessage, setShowLocationMessage] = useState(false);
  const [isOnShip, setIsOnShip] = useState(false);
  const [shipStatus, setShipStatus] = useState('');
  const [isSos, setIsSos] = useState(false);
  const [hasDamages, setHasDamages] = useState(false);
  const [damageDetails, setDamageDetails] = useState('');
  const [language, setLanguage] = useState('english'); // Add language state

  // Text based on current language
  const text = translations[language];

  // Toggle language between available options
  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  // Use appropriate direction options based on language
  const waveDirectionOptions = language === 'english' ? WAVE_DIRECTIONS : WAVE_DIRECTIONS_TAGALOG;
  const shipStatusOptions = language === 'english' ? SHIP_STATUSES : SHIP_STATUSES_TAGALOG;

  // Check if user has admin access based on email domain
  const hasAdminAccess = useMemo(() => {
    if (is_authenticated && user) {
      // Try different methods of accessing the email field
      const emailFromData = user.data?.email;
      const emailDirectly = user.email;
      
      // Use the email field based on what's available
      const userEmail = emailFromData || emailDirectly;
      
      if (userEmail) {
        const email = userEmail.toLowerCase();
        const adminDomains = ['.mil', '.gov', '.teambespin.us'];
        
        // Check for exact domain matches and the special case for @teambespin.us
        const hasPermission = adminDomains.some(domain => {
          if (domain === '.teambespin.us' && email.includes('@teambespin.us')) {
            return true;
          }
          return email.endsWith(domain);
        });
        
        return hasPermission;
      }
    }
    return false;
  }, [is_authenticated, user]);

  // Request location permission when component mounts
  useEffect(() => {
    getGeolocation();
  }, []);

  useEffect(() => {
    // Only update the UI after the component has mounted on the client
    setShowLocationMessage(true);
  }, []);

  const getGeolocation = () => {
    setLocationStatus('pending');
    
    if (!navigator.geolocation) {
      setLocationStatus('error');
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationStatus('success');
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationStatus('error');
        if (error.code === 1) {
          setErrorMessage('Location access denied. Your location will not be included in the report.');
        } else {
          setErrorMessage('Unable to get your location. Your location will not be included in the report.');
        }
      }
    );
  };

  const handleImageUpload = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Validate the file is an actual image
      if (!file.type.startsWith('image/')) {
        setErrorMessage(language === 'english' ? 
          'Please upload an image file (JPEG, PNG, etc.)' : 
          'Mangyaring mag-upload ng image file (JPEG, PNG, atbp.)');
        return;
      }
      
      // Validate file size
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setErrorMessage(language === 'english' ? 
          `Image is too large. Please upload an image smaller than 5MB (current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB)` : 
          `Masyadong malaki ang larawan. Mangyaring mag-upload ng larawang mas maliit sa 5MB (kasalukuyang laki: ${(file.size / (1024 * 1024)).toFixed(2)}MB)`);
        return;
      }
      
      console.log(`Valid image selected: ${file.name} (${file.type}, ${file.size} bytes)`);
      setUploadedImage(file);
    }
  };

  const handleSubmitClick = () => {
    // Check authentication first
    if (!is_authenticated) {
      requestSignIn();
      return;
    }
    
    // Validate required fields
    let isValid = true;
    let errorMsg = '';
    
    // Description is only required if SOS is not checked
    if (!isSos && !description.trim()) {
      errorMsg = language === 'english' ? 
        'Please provide a description of what you saw' : 
        'Mangyaring magbigay ng paglalarawan ng iyong nakita';
      isValid = false;
    } else if (isOnShip && !shipStatus) {
      errorMsg = language === 'english' ? 
        'Please select a ship status' : 
        'Mangyaring pumili ng katayuan ng barko';
      isValid = false;
    }
    
    if (!isValid) {
      setErrorMessage(errorMsg);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setConfirmDialogOpen(false);
    
    try {
      // Get Rownd access token directly from the hook
      const accessToken = await getAccessToken({ waitForToken: true });
      
      if (!accessToken) {
        throw new Error('Failed to get access token. Please try signing in again.');
      }

      // Create a FormData object to handle both text and image
      const formData = new FormData();
      
      // Prepare the report data
      const reportData = {
        description: description,
        reported_at: new Date().toISOString(),
      };
      
      // Merge the damage details with description for AI assessment if damage is reported
      if (hasDamages && damageDetails) {
        // Keep the original description separate for display purposes
        reportData.original_description = description;
        
        // Create merged description for AI assessment
        reportData.description = `${description}\n\nDAMAGE REPORT: ${damageDetails}`;
        
        // Store damage details separately
        reportData.damage_details = damageDetails;
      }
      
      // Add location data if available
      if (coordinates.latitude !== null && coordinates.longitude !== null) {
        reportData.latitude = coordinates.latitude;
        reportData.longitude = coordinates.longitude;
      }

      // Add optional fields if provided
      if (severity !== 5) reportData.severity = severity;  
      if (heightMeters) reportData.height_meters = parseFloat(heightMeters);
      if (waterLevel) reportData.water_level = parseFloat(waterLevel);
      if (waveDirection) reportData.wave_direction = waveDirection;
      
      // Add ship data if reporting from a ship
      if (isOnShip) {
        reportData.is_on_ship = true;
        if (shipStatus) {
          reportData.ship_status = shipStatus;
          // Add special location description for ships
          reportData.location_description = `On Ship, ${shipStatus}`;
        }
      }
      
      // Add SOS status if checked
      if (isSos) {
        reportData.status = "SOS";
      }
      
      // Add damage information if reported
      if (hasDamages) {
        reportData.has_damages = true;
      }
      
      // If user has admin access, automatically mark the report as verified
      if (hasAdminAccess) {
        reportData.verified = true;
        console.log('User has admin access - report automatically verified');
      }

      // Add report data as a JSON string in the 'data' field
      formData.append('data', JSON.stringify(reportData));
      
      // If there's an uploaded image, add it to the form data
      if (uploadedImage) {
        formData.append('images', uploadedImage);
      }
      
      // Use the API utility function instead of direct fetch for better offline handling
      const response = await submitReport(formData);
    
    // Show success message and reset form
    setSuccessMessage(true);
      resetForm();
      console.log('Report created successfully:', response.data);
    } catch (error) {
      // Check if the error is due to being offline
      if (error.message.includes('offline') || error.name === 'TypeError') {
        // This will be handled by the offline storage mechanism in api.js
        // Show a success message to the user
        setSuccessMessage(true);
        resetForm();
      } else {
        // Handle network or other errors
        setErrorMessage('Error submitting report: ' + error.message);
        console.error('Submission error:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setSeverity(5);
    setHeightMeters('');
    setWaterLevel('');
    setWaveDirection('');
    setMultilineValue('');
    setUploadedImage(null);
    setIsOnShip(false);
    setShipStatus('');
    setIsSos(false);
    setHasDamages(false);
    setDamageDetails('');
  };

  const handleCancelSubmit = () => {
    setConfirmDialogOpen(false);
  };

  const handleCloseSuccessMessage = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessMessage(false);
  };

  const handleCloseErrorMessage = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorMessage('');
  };

  // Helper function to get location status text and color
  const getLocationStatusInfo = () => {
    switch (locationStatus) {
      case 'success':
        return {
          text: 'Location detected',
          color: 'success.main'
        };
      case 'error':
        return {
          text: 'Location not available',
          color: 'error.main'
        };
      default:
        return {
          text: 'Detecting location...',
          color: 'text.secondary'
        };
    }
  };

  const locationStatusInfo = getLocationStatusInfo();

  // Modify success message content to check if offline mode
  const getSuccessMessageContent = () => {
    if (isSos) {
      return text.successSOS;
    }
    return text.successDefault;
  };

  // Add this function to handle setting preset locations
  const setPresetLocation = (lat, lng, label) => {
    setCoordinates({
      latitude: lat,
      longitude: lng
    });
    setLocationStatus('success');
    console.log(`Location set to ${label}: ${lat}, ${lng}`);
  };

  // Show authentication prompt when not authenticated
  if (!is_initializing && !is_authenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Stack direction="row" spacing={1}>
            <Button 
              variant={language === 'english' ? "contained" : "outlined"}
              size="small"
              onClick={() => changeLanguage('english')}
            >
              English
            </Button>
            <Button 
              variant={language === 'tagalog' ? "contained" : "outlined"}
              size="small"
              onClick={() => changeLanguage('tagalog')}
            >
              Tagalog
            </Button>
            <Button 
              variant={language === 'vietnamese' ? "contained" : "outlined"}
              size="small"
              onClick={() => changeLanguage('vietnamese')}
            >
              Tiếng Việt
            </Button>
          </Stack>
        </Box>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {text.authRequired}
          </Typography>
          <Typography variant="body1" paragraph>
            {text.signInText}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large" 
            startIcon={<LockOpenIcon />}
            onClick={() => requestSignIn()}
            sx={{ mt: 2 }}
          >
            {text.signInButton}
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        py: 3,
        px: { xs: 2, sm: 3, md: 4 },
        height: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: 'left',
            fontWeight: 'bold',
            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }
          }}
        >
          {text.pageTitle}
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <Button 
            variant={language === 'english' ? "contained" : "outlined"}
            size="small"
            onClick={() => changeLanguage('english')}
          >
            English
          </Button>
          <Button 
            variant={language === 'tagalog' ? "contained" : "outlined"}
            size="small"
            onClick={() => changeLanguage('tagalog')}
          >
            Tagalog
          </Button>
          <Button 
            variant={language === 'vietnamese' ? "contained" : "outlined"}
            size="small"
            onClick={() => changeLanguage('vietnamese')}
          >
            Tiếng Việt
          </Button>
        </Stack>
      </Box>
      
      <Grid 
        container 
        justifyContent="center" 
        alignItems="flex-start"
        sx={{ mt: 1 }}
        spacing={3}
      >
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {text.basicInfo}
            </Typography>
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {text.requiredField}
            </Typography>
            
            <InputField
              label={isSos ? text.describeOptionalLabel : text.describeLabel}
              placeholder={text.describePlaceholder}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={5}
              fullWidth
              required={!isSos}
              sx={{ mt: 0, mb: 3 }}
              error={errorMessage && !isSos && !description.trim()}
              helperText={(errorMessage && !isSos && !description.trim()) ? 
                text.requiredField : 
                `${description.length}/500 characters`
              }
              inputProps={{ maxLength: 500 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={isSos}
                  onChange={(e) => setIsSos(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {text.sosLabel}
                </Box>
              }
            />
          </Paper>
          
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {text.damageReport}
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={hasDamages}
                  onChange={(e) => setHasDamages(e.target.checked)}
                  color="warning"
                />
              }
              label={text.damageCheckbox}
              sx={{ mb: 2 }}
            />
            
            <Collapse in={hasDamages}>
              <InputField
                label={text.damageDetails}
                placeholder={text.damagePlaceholder}
                value={damageDetails}
                onChange={(e) => setDamageDetails(e.target.value)}
                multiline
                rows={3}
                fullWidth
                sx={{ mb: 2 }}
              />
            </Collapse>
          </Paper>
          
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {text.shipInfo}
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox 
                  checked={isOnShip}
                  onChange={(e) => setIsOnShip(e.target.checked)}
                  color="primary"
                />
              }
              label={text.reportFromShip}
              sx={{ mb: 2 }}
            />
            
            <Collapse in={isOnShip}>
              <FormControl 
                fullWidth 
                sx={{ mb: 2 }} 
                error={isOnShip && errorMessage && !shipStatus ? true : false}
              >
                <InputLabel id="ship-status-label">{text.shipStatus}</InputLabel>
                <Select
                  labelId="ship-status-label"
                  value={shipStatus}
                  label={text.selectStatus}
                  onChange={(e) => setShipStatus(e.target.value)}
                  required={isOnShip}
                  error={isOnShip && errorMessage && !shipStatus ? true : false}
                >
                  <MenuItem value=""><em>{text.selectStatus}</em></MenuItem>
                  {shipStatusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
                {isOnShip && errorMessage && !shipStatus && (
                  <FormHelperText>{text.shipStatusRequired}</FormHelperText>
                )}
              </FormControl>
            </Collapse>
          </Paper>
          
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {text.waveCharacteristics}
            </Typography>
            
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              {text.optionalFields}
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Typography gutterBottom>{text.severity}</Typography>
              <Slider
                value={severity}
                onChange={(_, newValue) => setSeverity(newValue)}
                valueLabelDisplay="auto"
                step={1}
                marks
                min={0}
                max={10}
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={text.waveHeight}
                  type="number"
                  placeholder="e.g., 3.5"
                  value={heightMeters}
                  onChange={(e) => setHeightMeters(e.target.value)}
                  inputProps={{ min: 0, step: 0.1 }}
                  fullWidth
                  sx={{ mb: 3 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputField
                  label={text.waterLevel}
                  type="number"
                  placeholder="e.g., 2.0"
                  value={waterLevel}
                  onChange={(e) => setWaterLevel(e.target.value)}
                  inputProps={{ step: 0.1 }}
                  fullWidth
                  sx={{ mb: 3 }}
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="wave-direction-label">{text.waveDirection}</InputLabel>
              <Select
                labelId="wave-direction-label"
                id="wave-direction"
                value={waveDirection}
                label={text.selectDirection}
                onChange={(e) => setWaveDirection(e.target.value)}
              >
                <MenuItem value=""><em>{text.selectDirection}</em></MenuItem>
                {waveDirectionOptions.map((direction) => (
                  <MenuItem key={direction} value={direction}>{direction}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
          
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {text.uploadImage}
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="center" 
              spacing={2} 
              sx={{ my: 2 }}
            >
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUploadIcon />}
                sx={{ 
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#135ba1' }
                }}
                disabled={isSubmitting}
              >
                {text.uploadImage}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                  disabled={isSubmitting}
                />
              </Button>
              
              <Tooltip title={locationStatus === 'error' ? text.getCurrentLocation : text.location}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<LocationOnIcon />}
                    onClick={getGeolocation}
                    disabled={isSubmitting || locationStatus === 'pending'}
                    sx={{ 
                      color: locationStatusInfo.color,
                      borderColor: locationStatusInfo.color
                    }}
                  >
                    {locationStatusInfo.text}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            
            {uploadedImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {text.imageRestriction}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {text.presetLocations}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {text.presetLocationsDesc}
              </Typography>
              
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  flexWrap: 'wrap', 
                  gap: 1,
                  '& > button': { minWidth: { xs: '45%', sm: 'auto' } }
                }}
              >
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setPresetLocation(14.5995, 120.9842, text.manila)}
                >
                  {text.manila}
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setPresetLocation(10.3157, 123.8854, text.cebu)}
                >
                  {text.cebu}
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => setPresetLocation(7.0707, 125.6087, text.davao)}
                >
                  {text.davao}
                </Button>
              </Stack>
            </Box>
            
            {showLocationMessage && (
              coordinates.latitude && coordinates.longitude ? (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="success.main">
                    {text.locationIncluded}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="error.main">
                    {text.locationNotAvailable}
                  </Typography>
                </Box>
              )
            )}
          </Paper>
          
          <Stack direction="row" justifyContent="center" sx={{ mt: 2, mb: 4 }}>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={handleSubmitClick}
                sx={{ 
                  px: 4,
                py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              >
              {isSubmitting ? text.submitting : text.submit}
              </Button>
            </Stack>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelSubmit}
      >
        <DialogTitle>{text.confirmSubmission}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {text.confirmText}
            {showLocationMessage && (
              coordinates.latitude && coordinates.longitude ? (
                <Box sx={{ mt: 2 }}>
                  {text.locationIncluded}
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {text.locationNotAvailable}
                </Box>
              )
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSubmit} color="primary">
            {text.cancel}
          </Button>
          <Button 
            onClick={handleConfirmSubmit} 
            color="primary" 
            variant="contained" 
            autoFocus
            disabled={isSubmitting}
          >
            {text.confirmButton}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar 
        open={successMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSuccessMessage} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {getSuccessMessageContent()}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar 
        open={!!errorMessage} 
        autoHideDuration={6000} 
        onClose={handleCloseErrorMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorMessage} 
          severity="error" 
          variant="filled"
          sx={{ width: '100%', fontSize: '1rem' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
} 